import { ProviderData } from "@/types/provider";
import { 
  Building2, 
  Clock, 
  Percent, 
  CheckCircle2,
  ShieldCheck,
  Wallet
} from "lucide-react";
import advisaLogo from "@/assets/advisa-logo.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import creditsafeImg from "@/assets/creditsafe.png";

export const advisaData: ProviderData = {
  id: "advisa",
  name: "Advisa",
  slug: "advisa",
  logo: advisaLogo,
  type: "comparison",
  numberOfLenders: 40,
  
  trustpilot: {
    rating: 4.6,
    reviews: 4212,
    url: "https://se.trustpilot.com/review/advisa.se"
  },
  
  hero: {
    badges: [
      { text: "Jämför 40 långivare med en ansökan", variant: "default" }
    ],
    description: "Advisa grundades 2011 och är en låneförmedlare som hjälper dig jämföra privatlån från 40 långivare. Smidig ansökningsprocess med omfattande support. Lån från 5 000 kr upp till 800 000 kr.",
    ctaText: "Ansök hos Advisa",
    ctaButtonText: "Ansök hos Advisa",
    ctaButtonVariant: "default"
  },
  
  stats: [
    {
      icon: Building2,
      value: "40 långivare",
      label: "Samarbetspartners",
      description: "Jämför erbjudanden"
    },
    {
      icon: Wallet,
      value: "800 000 kr",
      label: "Maxbelopp",
      description: "Från 5 000 kr"
    },
    {
      icon: Percent,
      value: "5,07 – 32,99 %",
      label: "Effektiv ränta",
      description: "Varierar"
    },
    {
      icon: Clock,
      value: "1 – 20 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Hos vissa långivare"
    }
  ],
  
  features: {
    pros: [
      "Smidig ansökningsprocess",
      "Omfattande support",
      "Samarbetar med många långivare"
    ],
    cons: [
      "Inget samarbete med storbanker"
    ]
  },
  
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: CheckCircle2,
      title: "Effektiv ränta",
      value: "5,07 – 32,99 %",
      description: "Varierar"
    },
    {
      image: creditsafeImg,
      title: "Medlemskap",
      value: "Creditsafe"
    }
  ],
  
  termsAndConditions: [
    {
      term: "Lånebelopp",
      value: "5 000 – 800 000 kr"
    },
    {
      term: "Nominell ränta",
      value: "4,50 – 22,00 %"
    },
    {
      term: "Effektiv ränta",
      value: "5,07 – 32,99 %"
    },
    {
      term: "Löptid",
      value: "1 – 20 år"
    },
    {
      term: "Uppläggningsavgift",
      value: "(0–995 kr) Varierar mellan långivare"
    },
    {
      term: "Lån utan UC",
      value: "Nej"
    },
    {
      term: "Svar på ansökan",
      value: "Samma dag"
    },
    {
      term: "Betalningslösning",
      value: "Banköverföring"
    }
  ],
  
  loanRequirements: [
    {
      requirement: "Legitimering",
      value: "Nej"
    },
    {
      requirement: "Folkbokförd i Sverige",
      value: "Ja"
    },
    {
      requirement: "Svenskt personnummer",
      value: "Nej"
    },
    {
      requirement: "Svenskt mobilnummer",
      value: "Nej"
    },
    {
      requirement: "Svenskt kontonummer",
      value: "Nej"
    },
    {
      requirement: "Minsta årsinkomst",
      value: "100 800 kr"
    },
    {
      requirement: "Betalningsanmärkning OK?",
      value: "Ja"
    },
    {
      requirement: "Ålderskrav",
      value: "18 +"
    }
  ],
  
  interestRateSection: {
    title: "Advisas ränta",
    description: "Hos Advisa är jämförelsetjänsten helt kostnadsfri. Det kostar ingenting för dig som kund att ansöka om lån. Det tillkommer inte heller några kostnader om du väljer att gå vidare med en långivare. Däremot tillkommer ränta och eventuell uppläggningsavgift beroende på vilken långivare du väljer.",
    rateTable: [
      {
        label: "Lånebelopp (lägsta – högsta)",
        value: "5 000 – 800 000 kr"
      },
      {
        label: "Nominell ränta",
        value: "4,50 – 22,00 %"
      },
      {
        label: "Effektiv ränta",
        value: "5,07 – 32,99 %"
      },
      {
        label: "Uppläggningsavgift",
        value: "Varierar mellan långivare"
      },
      {
        label: "Aviavgift",
        value: "Varierar mellan långivare"
      },
      {
        label: "Förtidsinlösen",
        value: "Kostnadsfri"
      }
    ]
  },
  
  payoutSection: {
    title: "När betalas lånet ut?",
    description: "Utbetalningstiden hos Advisa varierar beroende på vilken långivare som godkänner ditt lån.",
    payoutTable: [
      {
        timeframe: "Beviljad före 12:00",
        duration: "2-4 dagar"
      },
      {
        timeframe: "Beviljad efter 12:00",
        duration: "2-4 dagar"
      }
    ]
  },
  
  faq: [
    {
      question: "Accepterar långivarna hos Advisa betalningsanmärkningar?",
      answer: "Ja, ett fåtal långivare hos Advisa kan acceptera betalningsanmärkningar om de bedömer att du har bra nuvarande betalningsförmåga. Möjligheten att bli godkänd varierar mellan olika långivare och beror på din individuella ekonomiska situation."
    },
    {
      question: "Kan man låna utan UC via Advisa?",
      answer: "Nej, det går inte att låna pengar utan UC via Advisa. Alla långivare som Advisa samarbetar med kräver en UC-kreditupplysning för att bedöma din kreditvärdighet."
    },
    {
      question: "Vad får jag för ränta hos Advisa?",
      answer: "Räntan på ett privatlån sätts individuellt efter dina förutsättningar. Advisa hjälper dig att skicka en låneansökan till flera banker och långivare samtidigt för att du ska få reda på vilken som erbjuder dig bäst ränta."
    },
    {
      question: "Varför har jag fått flera brev från UC?",
      answer: "Enligt lag får man alltid omfrågandekopior från de parter som tagit del av ens kreditupplysning. Endast en kreditupplysning registreras hos UC och detta framgår även i breven."
    },
    {
      question: "När betalas lånet ut?",
      answer: "Ditt lån betalas ut 3-5 bankdagar efter att banken har mottagit ditt undertecknade avtal."
    },
    {
      question: "Kan jag samla mina lån och krediter hos Advisa?",
      answer: "Ja, Advisa kan hjälpa dig samla dina lån och krediter för att sänka din månadskostnad. Genom att samla lånen förbättrar du även din kreditvärdighet, vilket ger bättre lånevillkor i framtiden."
    },
    {
      question: "Hur funkar Advisa?",
      answer: "Att ansöka om lån hos Advisa är smidigt och kan göras via deras hemsida. Eftersom Advisa är en låneförmedlare ansöker du hos flera långivare samtidigt. Långivarna måste inte ge dig ett låneerbjudande, så det kan bli så att du inte får några erbjudanden."
    },
    {
      question: "Kostar det något att använda Advisa?",
      answer: "Nej, det är helt kostnadsfritt att jämföra lån hos Advisa. Ingen kostnad tillkommer för dig som kund, även om du väljer att teckna lån hos en långivare. Ränta och eventuell uppläggningsavgift varierar mellan långivare."
    },
    {
      question: "Vilken ränta kan man få genom Advisa?",
      answer: "Räntan varierar mellan 5,07% och 32,99% i effektiv ränta beroende på din kreditvärdighet, lånebelopp och återbetalningstid. Den exakta räntan sätts av långivarna som Advisa samarbetar med."
    },
    {
      question: "Hur mycket kan man låna hos Advisa?",
      answer: "Genom Advisa kan du ansöka om lån från 5 000 kr upp till 800 000 kr. Villkoren varierar mellan de 40 långivare som Advisa samarbetar med."
    }
  ],
  
  customerService: {
    description: "Advisa erbjuder kundservice via telefon och e-post för att hjälpa dig med frågor om din låneansökan och jämförelsetjänsten.",
    phone: "0770-175050",
    phoneHours: [
      "Vardagar: 09.00-20.30",
      "Lördag: 12.00-16.00",
      "Söndag: 12.00-16.00"
    ],
    email: "kundtjanst@advisa.se",
    emailHours: [
      "Svarar inom 24 timmar vardagar"
    ]
  },
  
  companyInfo: {
    name: "Advisa AB",
    orgNumber: "556974-8378",
    address: "Strandvägen 5B",
    city: "114 51 Stockholm",
    website: "https://www.advisa.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=127540"
  },
  
  affiliateLink: {
    linkId: "advisa-privatlan",
    trackingSource: "provider-page",
    url: "https://go.adt284.net/t/t?a=1923306192&as=2024680921&t=2&tk=1"
  },
  
  seo: {
    title: "Advisa Privatlån - Jämför 40 långivare | Svar samma dag",
    description: "Jämför privatlån från 40 långivare genom Advisa. Grundat 2011. Belopp 5 000-800 000 kr, effektiv ränta 5,07-32,99%. Svar samma dag. Ansök idag!",
    keywords: [
      "advisa",
      "jämför lån",
      "privatlån",
      "låneförmedlare",
      "40 långivare",
      "advisa recension"
    ]
  },
  
  schema: {
    productName: "Advisa Privatlån - Jämför 40 långivare",
    description: "Jämför privatlån från 40 långivare genom Advisa. Grundat 2011. Belopp 5 000-800 000 kr, effektiv ränta 5,07-32,99%. Svar samma dag.",
    url: "https://klarfinans.se/sv-SE/privatlan/advisa",
    provider: "Advisa",
    interestRate: {
      min: 5.07,
      max: 32.99
    },
    minAmount: 5000,
    maxAmount: 800000,
    loanTerm: ["1 år", "20 år"],
    featureList: [
      "Jämför lån från 40 olika långivare med en ansökan",
      "Lån från 5 000 kr upp till 800 000 kr",
      "Svar samma dag",
      "Flexibla återbetalningsvillkor 1-20 år",
      "Effektiv ränta 5,07 – 32,99 %",
      "Kan acceptera betalningsanmärkningar hos vissa långivare",
      "Kostnadsfri jämförelsetjänst",
      "Endast en UC-kreditupplysning trots flera erbjudanden"
    ],
    aggregateRating: {
      ratingValue: 4.6,
      reviewCount: 4212
    }
  }
};
